import React from 'react';
import { useAuth } from '@/context/AuthContext';
import { useUserProfile } from '@/hooks/useUserProfile';
import { MemberAvatar } from '@/components/MemberAvatar';

interface UserAvatarProps {
  /** Kept for the existing call sites; the signed-in member's record wins. */
  email?: string;
  name?: string;
  /** Stored profile image URL. Defaults to the signed-in member's `avatarUrl`. */
  avatarUrl?: string | null;
  size?: number;
  className?: string;
}

/**
 * The signed-in member's own avatar — the thread message rows and the sidebar
 * user menu. A thin wrapper that feeds the member record (photo from the PRO
 * sync, first/last name from the profile, colour theme from AR-393) into the
 * shared `MemberAvatar`.
 *
 * AR-409: this used to fall through to Gravatar, whose default "identicon" is
 * the geometric image members saw instead of a photo. Gravatar is gone from
 * member avatars: photo, else initials on the member's theme colour.
 */
export function UserAvatar({ email, name, avatarUrl, size = 32, className = '' }: UserAvatarProps) {
  const { user } = useAuth();
  const { firstName, lastName } = useUserProfile();

  return (
    <MemberAvatar
      photoUrl={avatarUrl ?? user?.avatarUrl ?? null}
      firstName={firstName}
      lastName={lastName}
      displayName={name || user?.displayName}
      email={email || user?.email}
      size={size}
      className={className}
    />
  );
}

export default UserAvatar;
